import type { DateRange } from 'react-day-picker'
import type { WidgetPeriods } from '../widgets/types'

import { useCallback, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { ArrowRightLeftIcon, CalendarIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
	createStaticRange,
	DateRangeMenuPanel,
	defaultPresets,
	useRangePreset,
} from '@/shared/components/ui/date-picker-ranges'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { DateFormat, getUTCDate, intlFormat } from '@/shared/tools/dates'

import { getLocalization } from './localization'

type MultiPeriodsSelectProps = {
	periods: WidgetPeriods
	defaultPeriods: WidgetPeriods
	onChangePeriods: (periods: WidgetPeriods) => void
	maxDate?: Date
}
const toRangeValues = (value: {
	start_date: string
	end_date: string
}): DateRange => ({
	from: getUTCDate(new Date(value.start_date)),
	to: getUTCDate(new Date(value.end_date)),
})

export default function MultiPeriodsSelect({
	periods,
	defaultPeriods,
	onChangePeriods,
	maxDate = new Date(),
}: MultiPeriodsSelectProps) {
	const values = useMemo(
		() => ({
			main: toRangeValues(periods.main),
			compared: toRangeValues(periods.compared),
		}),
		[periods]
	)
	const defaultValues = useMemo(
		() => ({
			main: toRangeValues(defaultPeriods.main),
			compared: toRangeValues(defaultPeriods.compared),
		}),
		[defaultPeriods]
	)
	const handleMainChanged = useCallback(
		(range: DateRange) =>
			onChangePeriods({
				compared: {
					start_date: format(new Date(range.from as Date), DateFormat.system),
					end_date: format(new Date(range.to as Date), DateFormat.system),
				},
				main: {
					start_date: format(new Date(range.from as Date), DateFormat.system),
					end_date: format(new Date(range.to as Date), DateFormat.system),
				},
			}),
		[values]
	)
	const handleComparedChanged = useCallback(
		(range: DateRange) => {
			onChangePeriods({
				...periods,
				compared: {
					start_date: format(range.from as Date, DateFormat.system),
					end_date: format(range.to as Date, DateFormat.system),
				},
			})
		},
		[values]
	)

	return (
		<div className='flex items-center space-x-1'>
			<MainPeriodPicker
				value={values.main}
				defaultValue={defaultValues.main}
				setValue={handleMainChanged}
				title='Main period'
				maxDate={maxDate}
			/>
			<ComparePeriodPicker
				value={values.compared}
				defaultValue={defaultValues.compared}
				setValue={handleComparedChanged}
				title='Compared period'
				maxDate={maxDate}
			/>
		</div>
	)
}

function MainPeriodPicker({
	value,
	setValue,
	title,
	defaultValue,
	maxDate,
}: {
	value: DateRange
	setValue: (value: DateRange) => void
	title: string
	defaultValue: DateRange
	maxDate: Date
}) {
	const { t } = useTranslation()

	const [open, setOpen] = useState(false)
	const staticRanges = createStaticRange(defaultPresets)
	const selected = useRangePreset({
		value,
		staticRanges,
	})
	const selectedLabel = useMemo(() => {
		if (!selected) return null
		if (selected.label) return selected.label

		return (
			intlFormat(selected.range.from!) + ' - ' + intlFormat(selected.range.to!)
		)
	}, [selected])

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button>
					<CalendarIcon />
					{selectedLabel ||
						t('calendar.input.select_dates', { defaultValue: 'Select dates' })}
				</Button>
			</PopoverTrigger>
			<PopoverContent className='flex w-auto flex-col p-0'>
				<div className='border-b px-4 py-2 text-sm font-semibold'>{title}</div>
				<DateRangeMenuPanel
					localization={getLocalization(t)}
					value={value}
					defaultValue={defaultValue}
					maxDate={maxDate}
					defaultPresets={staticRanges}
					onValueChange={(range) => {
						setValue(range)
						setOpen(false)
					}}
				/>
			</PopoverContent>
		</Popover>
	)
}
function ComparePeriodPicker({
	value,
	setValue,
	title,
	defaultValue,
	maxDate,
}: {
	value: DateRange
	setValue: (value: DateRange) => void
	title: string
	defaultValue: DateRange
	maxDate: Date
}) {
	const [open, setOpen] = useState(false)
	const staticRanges = createStaticRange(defaultPresets)
	const selected = useRangePreset({
		value,
		staticRanges,
	})

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant='ghost'>
					<ArrowRightLeftIcon />
					{selected?.label || 'Select dates'}
				</Button>
			</PopoverTrigger>
			<PopoverContent className='flex w-auto flex-col p-0'>
				<div className='border-b px-4 py-2 text-sm font-semibold'>{title}</div>
				<DateRangeMenuPanel
					value={value}
					maxDate={maxDate}
					defaultValue={defaultValue}
					defaultPresets={staticRanges}
					onValueChange={(range) => {
						setValue(range)
						setOpen(false)
					}}
				/>
			</PopoverContent>
		</Popover>
	)
}
